import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useAuth } from '../../../hooks/auth';
import {
  BiComment,
  BiCommentStatus,
  BiCommentType,
  BiReport,
  biErrorMessage,
  biService,
} from '../../../services/biService';
import { canManageBi } from '../../../utils/biAccess';
import { apllyToast } from '../../Global/Toast2.0';

const MAX_LENGTH = 2000;

export const COMMENT_TYPES: Record<
  BiCommentType,
  { label: string; color: string }
> = {
  duvida: { label: 'Dúvida', color: 'blue' },
  sugestao: { label: 'Sugestão', color: 'purple' },
  erro: { label: 'Erro nos dados', color: 'red' },
};

const COMMENT_STATUS: Record<
  BiCommentStatus,
  { label: string; color: string }
> = {
  aberto: { label: 'Aberto', color: 'orange' },
  resolvido: { label: 'Resolvido', color: 'green' },
};

export function biCommentsKey(reportId: string) {
  return ['bi-comments', reportId];
}

function formatDate(date: string) {
  return format(new Date(date), "dd/MM/yyyy 'às' HH:mm");
}

interface Props {
  report: BiReport;
  currentPage?: string;
}

function CommentBody({ comment }: { comment: BiComment }) {
  return (
    <Flex gridGap="3" align="flex-start">
      <Avatar name={comment.author?.name || '?'} size="sm" />
      <Box flex="1" minW="0">
        <Flex justify="space-between" gridGap="2" wrap="wrap">
          <Text fontWeight="medium" fontSize="sm">
            {comment.author?.name || 'Usuário removido'}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {formatDate(comment.created_at)}
          </Text>
        </Flex>
        <Text fontSize="sm" mt="1" whiteSpace="pre-wrap" wordBreak="break-word">
          {comment.content}
        </Text>
      </Box>
    </Flex>
  );
}

function CommentThread({
  comment,
  canManage,
  onReply,
  onChangeStatus,
}: {
  comment: BiComment;
  canManage: boolean;
  onReply: (parent: BiComment, content: string) => Promise<void>;
  onChangeStatus: (target: BiComment, status: BiCommentStatus) => void;
}) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const type = COMMENT_TYPES[comment.type] || COMMENT_TYPES.duvida;
  const status = COMMENT_STATUS[comment.status] || COMMENT_STATUS.aberto;
  const isOpen = comment.status === 'aberto';

  const handleToggleStatus = () =>
    onChangeStatus(comment, isOpen ? 'resolvido' : 'aberto');

  const handleReply = async (event: FormEvent) => {
    event.preventDefault();

    if (!reply.trim()) {
      return;
    }

    setSending(true);
    try {
      await onReply(comment, reply.trim());
      setReply('');
      setReplying(false);
    } catch {
      // O aviso de erro já foi mostrado; a resposta fica no campo.
    } finally {
      setSending(false);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p="3">
      <HStack spacing="2" mb="3" wrap="wrap">
        <Badge colorScheme={type.color}>{type.label}</Badge>
        <Badge colorScheme={status.color} variant="outline">
          {status.label}
        </Badge>
        {comment.page_name && (
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {comment.page_name}
          </Text>
        )}
      </HStack>

      <CommentBody comment={comment} />

      {comment.replies.length > 0 && (
        <Stack
          spacing="3"
          mt="3"
          ml="4"
          pl="3"
          borderLeftWidth="2px"
          borderColor="gray.700"
        >
          {comment.replies.map(item => (
            <CommentBody key={item.id} comment={item} />
          ))}
        </Stack>
      )}

      {replying ? (
        <Box as="form" onSubmit={handleReply} mt="3">
          <Textarea
            size="sm"
            value={reply}
            onChange={event => setReply(event.target.value)}
            maxLength={MAX_LENGTH}
            placeholder="Escreva a resposta"
            aria-label="Resposta"
            autoFocus
          />
          <HStack justify="flex-end" mt="2">
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setReplying(false)}
            >
              Cancelar
            </Button>
            <Button
              size="xs"
              colorScheme="blue"
              type="submit"
              isLoading={sending}
              isDisabled={!reply.trim()}
            >
              Responder
            </Button>
          </HStack>
        </Box>
      ) : (
        <HStack spacing="2" mt="3">
          <Button size="xs" variant="ghost" onClick={() => setReplying(true)}>
            Responder
          </Button>
          {canManage && (
            <Button
              size="xs"
              variant="ghost"
              colorScheme={isOpen ? 'green' : 'gray'}
              onClick={handleToggleStatus}
            >
              {isOpen ? 'Marcar como resolvido' : 'Reabrir'}
            </Button>
          )}
        </HStack>
      )}
    </Box>
  );
}

export function CommentsPanel({ report, currentPage }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [type, setType] = useState<BiCommentType>('duvida');
  const [content, setContent] = useState('');

  const comments = useQuery(biCommentsKey(report.id), () =>
    biService.listComments(report.id),
  );

  const refresh = () => queryClient.invalidateQueries(biCommentsKey(report.id));

  const createComment = useMutation(biService.createComment, {
    onSuccess: refresh,
    onError: err =>
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível enviar o comentário.'),
      ),
  });

  const changeStatus = useMutation(
    ({ id, status }: { id: string; status: BiCommentStatus }) =>
      biService.updateCommentStatus(id, status),
    {
      onSuccess: refresh,
      onError: err =>
        apllyToast(
          'error',
          biErrorMessage(err, 'Não foi possível alterar a situação.'),
        ),
    },
  );

  const isAdmin = canManageBi(user);
  const isOwner = !!report.owner && report.owner.id === user?.id;

  const handleChangeStatus = (target: BiComment, status: BiCommentStatus) =>
    changeStatus.mutate({ id: target.id, status });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!content.trim()) {
      return;
    }

    try {
      await createComment.mutateAsync({
        power_bi_report_id: report.id,
        content: content.trim(),
        type,
        page_name: currentPage || null,
      });
      setContent('');
      apllyToast('success', 'Comentário enviado.');
    } catch {
      // O aviso de erro já foi mostrado; o texto fica no campo.
    }
  };

  const handleReply = async (parent: BiComment, text: string) => {
    await createComment.mutateAsync({
      power_bi_report_id: report.id,
      content: text,
      parent_id: parent.id,
      page_name: currentPage || null,
    });
  };

  return (
    <Stack spacing="5">
      <Box as="form" onSubmit={handleSubmit}>
        <Select
          size="sm"
          value={type}
          onChange={event => setType(event.target.value as BiCommentType)}
          aria-label="Tipo do comentário"
          mb="2"
        >
          {(Object.keys(COMMENT_TYPES) as BiCommentType[]).map(key => (
            <option key={key} value={key}>
              {COMMENT_TYPES[key].label}
            </option>
          ))}
        </Select>
        <Textarea
          value={content}
          onChange={event => setContent(event.target.value)}
          maxLength={MAX_LENGTH}
          placeholder="Tem uma dúvida, sugestão ou viu um número estranho? Escreva aqui."
          aria-label="Comentário"
          rows={3}
        />
        <Flex justify="space-between" align="center" mt="2">
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {currentPage ? `Página: ${currentPage}` : ''}
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            type="submit"
            isLoading={createComment.isLoading}
            isDisabled={!content.trim()}
          >
            Enviar
          </Button>
        </Flex>
      </Box>

      {comments.isLoading && (
        <Center py="6">
          <Spinner color="blue.500" />
        </Center>
      )}

      {comments.isError && (
        <Flex align="center" justify="space-between" gridGap="2">
          <Text fontSize="sm" color="gray.400">
            Não foi possível carregar os comentários.
          </Text>
          <Button size="xs" onClick={() => comments.refetch()}>
            Tentar novamente
          </Button>
        </Flex>
      )}

      {comments.data && comments.data.length === 0 && (
        <Text fontSize="sm" color="gray.500" textAlign="center" py="4">
          Nenhum comentário ainda.
        </Text>
      )}

      {comments.data?.map(comment => (
        <CommentThread
          key={comment.id}
          comment={comment}
          canManage={isAdmin || isOwner || comment.author?.id === user?.id}
          onReply={handleReply}
          onChangeStatus={handleChangeStatus}
        />
      ))}
    </Stack>
  );
}
